// src/utils/speechUtils.js
let currentUtterance = null;
let isSpeechEnabled = JSON.parse(localStorage.getItem("isAudioModeOn")) ?? true;

// Utility: detect if text contains Gurmukhi (Punjabi) characters
const containsGurmukhi = (text = "") => {
  // Unicode block for Gurmukhi is U+0A00–U+0A7F
  return /[\u0A00-\u0A7F]/.test(text);
};

// Utility: choose a voice based on preferred language codes (in order)
const chooseVoice = (voices, preferredLangs = []) => {
  // normalize langs to lowercase for comparison
  const langs = preferredLangs.map((l) => l.toLowerCase());

  // 1) exact lang match priority (pa-IN -> pa)
  for (const lang of langs) {
    const exact = voices.find((v) => v.lang && v.lang.toLowerCase() === lang);
    if (exact) return exact;
  }

  // 2) startsWith lang match (e.g., 'pa' matches 'pa-IN' etc.)
  for (const lang of langs) {
    const starts = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(lang.split('-')[0]));
    if (starts) return starts;
  }

  // 3) fallback by name keywords (some voices don't correctly tag lang)
  for (const lang of langs) {
    const byName = voices.find((v) =>
      v.name && v.name.toLowerCase().includes(lang.split('-')[0])
    );
    if (byName) return byName;
  }

  // 4) last resort: return first voice that looks Indian / english-ish
  return (
    voices.find((v) => v.lang && v.lang.toLowerCase().includes("en-in")) ||
    voices.find((v) => v.lang && v.lang.toLowerCase().includes("en")) ||
    voices[0] ||
    null
  );
};

// Debug helper — call in the console to list available voices
export const logAvailableVoices = () => {
  const voices = speechSynthesis.getVoices();
  console.table(
    voices.map((v) => ({
      name: v.name,
      lang: v.lang,
      localService: v.localService,
      default: v.default,
    }))
  );
};

// Main speak function — auto-detects Punjabi/Gurmukhi and picks best voice
export const speakText = (text) => {
  if (!isSpeechEnabled) return;
  if (!text) return;

  if (currentUtterance) {
    speechSynthesis.cancel();
  }

  currentUtterance = new SpeechSynthesisUtterance(text);

  const speakWithSelectedVoice = () => {
    const voices = speechSynthesis.getVoices() || [];

    // Decide preferred languages order depending on script
    const wantsPunjabi = containsGurmukhi(text);
    const preferred = wantsPunjabi
      ? ["pa-IN", "pa", "hi-IN", "hi", "en-IN", "en"]
      : ["en-IN", "en", "hi-IN", "hi", "pa-IN", "pa"];

    // Try to pick a voice in that order
    const voice = chooseVoice(voices, preferred);

    if (voice) {
      currentUtterance.voice = voice;
      // Also set lang on utterance to help some platforms
      currentUtterance.lang = voice.lang || (wantsPunjabi ? "pa-IN" : "en-IN");
    } else {
      // final fallback
      currentUtterance.lang = wantsPunjabi ? "pa-IN" : "en-IN";
      console.warn("No matching voice found — using lang fallback:", currentUtterance.lang);
    }

    // optional: tweak rate/pitch if you want
    currentUtterance.rate = 1; // default; expose setter if needed
    currentUtterance.pitch = 1;

    speechSynthesis.speak(currentUtterance);
  };

  // Voices may not be available immediately — ensure we wait
  if (speechSynthesis.getVoices().length > 0) {
    speakWithSelectedVoice();
  } else {
    // onvoiceschanged may fire multiple times; set handler to run once
    const onVoicesChanged = () => {
      speakWithSelectedVoice();
      speechSynthesis.onvoiceschanged = null;
    };
    speechSynthesis.onvoiceschanged = onVoicesChanged;
  }
};

// Keep the rate update behavior
export const updateSpeechRate = (rate) => {
  if (currentUtterance) {
    currentUtterance.rate = rate;
  }
};

export const stopSpeech = () => {
  if (currentUtterance) {
    speechSynthesis.cancel();
    currentUtterance = null;
  }
};

export const enableSpeech = () => {
  isSpeechEnabled = true;
  localStorage.setItem("isAudioModeOn", JSON.stringify(true));
};

export const disableSpeech = () => {
  isSpeechEnabled = false;
  localStorage.setItem("isAudioModeOn", JSON.stringify(false));
};

// conditionalSpeakText kept similar — but uses same selection logic
export const conditionalSpeakText = (text, isPlaying) => {
  if (!isSpeechEnabled || isPlaying) return;

  speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);

  const speakFn = () => {
    const voices = speechSynthesis.getVoices() || [];
    const wantsPunjabi = containsGurmukhi(text);
    const preferred = wantsPunjabi
      ? ["pa-IN", "pa", "hi-IN", "hi", "en-IN", "en"]
      : ["en-IN", "en", "hi-IN", "hi", "pa-IN", "pa"];

    const voice = chooseVoice(voices, preferred);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang || (wantsPunjabi ? "pa-IN" : "en-IN");
    } else {
      utterance.lang = wantsPunjabi ? "pa-IN" : "en-IN";
    }
    speechSynthesis.speak(utterance);
  };

  if (speechSynthesis.getVoices().length > 0) {
    speakFn();
  } else {
    speechSynthesis.onvoiceschanged = speakFn;
  }
};
